import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

// Mock fetching related records since actual Firestore fetch requires specific DB structure
const useRelatedRecords = (moduleId, search) => {
    // In a real app, this would query Firestore collection `modules/${moduleId}/records`
    const [records, setRecords] = useState([]);
    
    useEffect(() => {
        // Mock data
        const mockData = [
            { id: 'REC-001', title: 'Acme Corp Deal' },
            { id: 'REC-002', title: 'Stark Industries' },
            { id: 'REC-003', title: 'Wayne Enterprises Renewal' }
        ];
        
        if (!search) {
            setRecords(mockData);
        } else {
            setRecords(mockData.filter(r => 
                r.title.toLowerCase().includes(search.toLowerCase()) || 
                r.id.toLowerCase().includes(search.toLowerCase())
            ));
        }
    }, [moduleId, search]);

    return records;
};

const RelationPicker = ({ moduleId, value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef(null);
    const records = useRelatedRecords(moduleId, search);

    const valArray = Array.isArray(value) ? value : (value ? [value] : []);

    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isOpen]);

    const toggleRecord = (id) => {
        const newVals = valArray.includes(id)
            ? valArray.filter(v => v !== id)
            : [...valArray, id];
        onChange(newVals);
    };

    const renderSelectedChips = () => {
        if (valArray.length === 0) {
            return <span style={{ color: 'var(--text-tertiary)' }}>Select relation...</span>;
        }

        return (
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {valArray.map(id => {
                    // Try to find title from current results or fallback to ID
                    const rec = records.find(r => r.id === id) || { title: id };
                    return (
                        <div key={id} style={{
                            display: 'flex', alignItems: 'center', gap: '4px',
                            background: 'var(--bg-muted)',
                            border: '1px solid var(--border)',
                            color: 'var(--text-primary)',
                            padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 500
                        }}>
                            <ObliqueIcon name="link" size={10} color="var(--text-tertiary)" />
                            {rec.title}
                            <div 
                                style={{ display: 'flex', cursor: 'pointer', opacity: 0.5, marginLeft: '2px' }} 
                                onClick={(e) => { e.stopPropagation(); toggleRecord(id); }}
                                className="hover-opacity-100 hover-color-danger"
                            >
                                <ObliqueIcon name="close" size={12} />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div ref={containerRef} style={{ position: 'relative', width: '100%', minWidth: '160px' }}>
            <div 
                onClick={() => setIsOpen(!isOpen)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={{
                    minHeight: '28px', width: '100%',
                    padding: '2px 8px',
                    border: `1px solid ${isOpen || isHovered ? 'var(--border-strong)' : 'var(--border)'}`,
                    borderRadius: '4px',
                    background: 'var(--bg-surface)',
                    display: 'flex', alignItems: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                    transition: 'border-color 0.15s ease-in-out',
                    boxSizing: 'border-box'
                }}
            >
                {renderSelectedChips()}
            </div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 4, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 4, scale: 0.98 }}
                        transition={{ duration: 0.15 }}
                        style={{
                            position: 'absolute', top: 'calc(100% + 4px)', left: 0,
                            minWidth: '240px',
                            background: 'var(--bg-surface)', border: '1px solid var(--border)',
                            borderRadius: '8px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                            zIndex: 3000, padding: '8px',
                            display: 'flex', flexDirection: 'column', gap: '8px'
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', padding: '0 8px', background: 'var(--bg-subtle)', borderRadius: '6px', border: '1px solid var(--border)' }}>
                            <ObliqueIcon name="search" size={14} color="var(--text-tertiary)" />
                            <input
                                autoFocus
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={`Search related ${moduleId || 'records'}...`}
                                style={{
                                    border: 'none', background: 'transparent', outline: 'none',
                                    padding: '6px 8px', fontSize: '12px', width: '100%',
                                    color: 'var(--text-primary)'
                                }}
                            />
                        </div>

                        <div style={{ maxHeight: '200px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px' }} className="custom-scrollbar">
                            {records.length === 0 ? (
                                <div style={{ padding: '8px', fontSize: '12px', color: 'var(--text-tertiary)', textAlign: 'center' }}>
                                    No records found
                                </div>
                            ) : (
                                records.map(rec => (
                                    <div
                                        key={rec.id}
                                        onClick={() => toggleRecord(rec.id)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '10px',
                                            padding: '6px 8px', borderRadius: '6px', cursor: 'pointer',
                                            background: valArray.includes(rec.id) ? 'var(--bg-muted)' : 'transparent'
                                        }}
                                        className="hover-bg-muted"
                                    >
                                        <div style={{ width: '16px', color: 'var(--accent)', display: 'flex', justifyContent: 'center' }}>
                                            {valArray.includes(rec.id) && <ObliqueIcon name="check" size={14} />}
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>{rec.title}</span>
                                            <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{rec.id}</span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default RelationPicker;
